import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import {
  chordInventory, lyricLines, normalizeKey, transposeKey,
} from '../../lib/chords'
import { toTitleCase } from '../../lib/format'
import { Shell } from '../../components/Shell'
import ChordChart from '../../components/ChordChart'
import { Spinner, sectionLabel } from '../../components/ui'

/**
 * The sheet a team takes to a rehearsal: chart or words, on paper.
 *
 * Taking it opens the sheet full screen first, rather than firing the print
 * dialog from under the app chrome. That view is the thing itself at reading
 * size — useful on a music stand at rehearsal, not only as a staging post on
 * the way to paper — and saving is one button inside it.
 *
 * The PDF is the browser's, via print-to-PDF, not a bundled generator. Three
 * reasons that is the right call rather than the lazy one: a PDF library is
 * ~300KB on a bundle that is already too big for a phone on church wifi; the
 * page has to look right on screen anyway, so a print stylesheet is work that
 * was already needed; and the output uses the reader's own paper size and
 * fonts, which a hardcoded A4 canvas gets wrong for half the world.
 *
 * (Planning Center export would slot in beside the print button — it wants this
 * same structure as ChordPro. Not built here.)
 */
export default function Sheet() {
  const { id } = useParams()
  const nav = useNavigate()
  const { user } = useSession()

  const [song, setSong] = useState(null)
  const [sections, setSections] = useState([])
  const [writers, setWriters] = useState([])
  const [takes, setTakes] = useState(null)
  const [ready, setReady] = useState(false)
  const [err, setErr] = useState(null)
  const [lockReason, setLockReason] = useState(null)

  const [kind, setKind] = useState('chart')   // chart | lyrics
  const [mode, setMode] = useState('chords')  // chords | numbers
  const [steps, setSteps] = useState(0)
  const [logged, setLogged] = useState(false)
  const [taking, setTaking] = useState(false)

  const load = useCallback(async () => {
    const [{ data: s, error }, { data: sec }, { data: cw }] = await Promise.all([
      supabase.from('songs').select('*, users:songwriter_id(first_name, last_name)')
        .eq('id', id).maybeSingle(),
      supabase.from('song_sections').select('*').eq('song_id', id).order('order_position'),
      supabase.from('song_cowriters').select('cowriter_name, split_percentage')
        .eq('song_id', id).order('is_primary', { ascending: false }),
    ])
    if (error) setErr(readableError(error))
    setSong(s ?? null)
    setSections(sec ?? [])
    setWriters(cw ?? [])
    setReady(true)

    if (!s) {
      supabase.rpc('song_church_lock_reason', { p_song: id })
        .then(({ data }) => setLockReason(data ?? null))
    }
  }, [id])

  useEffect(() => { load() }, [load])

  const mine = song && song.songwriter_id === user.id

  // The writer's own count. Loaded separately because the RPC returns nothing
  // useful to anyone else, and asking for it would just be a wasted round trip.
  useEffect(() => {
    if (!mine) return
    supabase.rpc('song_export_counts', { p_song: id })
      .then(({ data }) => setTakes(data?.[0] ?? null))
  }, [mine, id])

  // Escape closes it, and the page underneath must not scroll while it's up —
  // otherwise dismissing the sheet drops you somewhere other than where you were.
  useEffect(() => {
    if (!taking) return
    const onKey = (e) => { if (e.key === 'Escape') setTaking(false) }
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [taking])

  const written = normalizeKey(song?.key) ?? 'C'
  const sounding = useMemo(() => transposeKey(written, steps), [written, steps])
  const inventory = useMemo(
    () => chordInventory(sections, { mode, key: sounding }),
    [sections, mode, sounding]
  )

  if (!ready) return <Shell><Spinner /></Shell>
  if (!song) {
    return (
      <Shell back="/collective" title="The collective">
        {lockReason === 'withdrawn' ? (
          <>
            <h2 className="q">This song is locked.</h2>
            <p className="sub">
              Access to this song is currently locked due to a request by the song's owner.
              Contact your church to discuss.
            </p>
          </>
        ) : (
          <>
            <h2 className="q">This song isn't open to you.</h2>
            <p className="sub">The writer may have pulled it back in.</p>
          </>
        )}
      </Shell>
    )
  }

  const writerName = [song.users?.first_name, song.users?.last_name].filter(Boolean).join(' ')
  const credits = writers.length
    ? writers.map((w) => w.cowriter_name).join(' · ')
    : writerName || 'Unknown writer'

  /**
   * Log first, then open it. The take is recorded when someone commits to
   * leaving with the sheet, which is this click — neither the full-screen view
   * nor the print dialog after it reports back whether they went through with
   * it. Counting the intent is the honest thing available, and a failed log
   * must never stop someone taking the sheet.
   */
  const takeIt = async () => {
    if (!mine && !logged) {
      setLogged(true)
      const { error } = await supabase.rpc('log_song_export', { p_song: song.id, p_kind: kind })
      if (error) console.warn('export not logged', error)
    }
    setTaking(true)
  }

  /* The sheet itself — rendered in exactly one place at a time. Two copies in
     the DOM would put the song on paper twice. */
  const sheetArticle = (
    <article className="sheet-page">
      <header className="sheet-head">
        <div>
          <h1 className="sheet-title">{toTitleCase(song.title) || 'Untitled'}</h1>
          <p className="sheet-credit">{credits}</p>
        </div>
        <div className="sheet-meta">
          {song.song_number && <span><b>Ref</b> {song.song_number}</span>}
          {kind === 'chart' && <span><b>Key</b> {mode === 'numbers' ? `${written} (numbers)` : sounding}</span>}
          {song.tempo && <span><b>Tempo</b> {song.tempo}</span>}
          {song.scripture_refs?.[0] && <span><b>Scripture</b> {song.scripture_refs[0]}</span>}
        </div>
      </header>

      {kind === 'chart' && inventory.length > 0 && (
        <p className="sheet-inv">
          <b>Chords</b> {inventory.join('   ')}
        </p>
      )}

      {sections.length === 0 ? (
        <p className="small">This song has no written sections.</p>
      ) : kind === 'chart' ? (
        <ChordChart sections={sections} mode={mode} songKey={sounding} />
      ) : (
        <div className="chart">
          {sections.map((s) => (
            <section className="chart-sec" key={s.id}>
              <h3 className="chart-sec-label">{sectionLabel(s)}</h3>
              {lyricLines(s.lyrics).map((l, i) => (
                l.text.trim()
                  ? <p className="sheet-lyric" key={i}>{l.text}</p>
                  : <div className="chart-gap" key={i} aria-hidden="true" />
              ))}
            </section>
          ))}
        </div>
      )}

      <footer className="sheet-foot">
        <span className="sheet-mark">Beseter</span>
        <p>
          Shared by {credits} through Beseter. Given to you for your team's
          rehearsal and worship use. It is not a licence to record, publish,
          or reproduce this song for sale — those rights stay with the writers,
          and permission for anything beyond your own gatherings has to come
          from them.
        </p>
        <p className="sheet-foot-thin">
          {song.allow_covers
            ? 'The writers have said another church may record their own version, credited to them.'
            : 'The writers have not opened this to covers.'}
          {' '}Printed {new Date().toLocaleDateString()}.
        </p>
      </footer>
    </article>
  )

  return (
    <Shell back={mine ? '/me' : `/songs/${song.id}/learn`} title="Back">
      {/* Controls. Hidden on paper — see @media print. */}
      <div className="noprint">
        <div className="eyebrow">Take it with you</div>
        <h2 className="q">{kind === 'chart' ? 'Chord chart' : 'Lyric sheet'}</h2>
        <p className="sub">
          Opens full screen to read from, and saves as a PDF from there. It carries
          the writers' names and the usage note with it.
        </p>

        <div className="chart-bar">
          <div className="seg" role="group" aria-label="Sheet kind">
            <button className={`seg-b${kind === 'chart' ? ' on' : ''}`}
                    onClick={() => setKind('chart')}>Chords</button>
            <button className={`seg-b${kind === 'lyrics' ? ' on' : ''}`}
                    onClick={() => setKind('lyrics')}>Words only</button>
          </div>
          {kind === 'chart' && (
            <div className="seg" role="group" aria-label="Notation">
              <button className={`seg-b${mode === 'chords' ? ' on' : ''}`}
                      onClick={() => setMode('chords')}>Letters</button>
              <button className={`seg-b${mode === 'numbers' ? ' on' : ''}`}
                      onClick={() => setMode('numbers')}>Numbers</button>
            </div>
          )}
        </div>

        {kind === 'chart' && mode === 'chords' && (
          <div className="chart-bar">
            <span className="small">Take it in</span>
            <div className="transpose">
              <button className="iconbtn" onClick={() => setSteps(steps - 1)}
                      aria-label="Down a semitone">−</button>
              <span className="transpose-k">
                {sounding}{steps !== 0 && <small>from {written}</small>}
              </span>
              <button className="iconbtn" onClick={() => setSteps(steps + 1)}
                      aria-label="Up a semitone">+</button>
            </div>
          </div>
        )}

        <button className="btn btn-primary" onClick={takeIt}>
          {kind === 'chart' ? 'Open the chart' : 'Open the words'}
        </button>

        {mine && (
          <p className="small" style={{ marginTop: 12 }}>
            {takes && Number(takes.people) > 0
              ? <>
                  <b>{takes.people} {Number(takes.people) === 1 ? 'person has' : 'people have'}</b>{' '}
                  taken this away — {takes.charts} chart{Number(takes.charts) === 1 ? '' : 's'},{' '}
                  {takes.lyrics} lyric sheet{Number(takes.lyrics) === 1 ? '' : 's'}.
                  {takes.last_at && ` Last on ${new Date(takes.last_at).toLocaleDateString()}.`}
                </>
              : 'Nobody outside your own account has taken this yet. Your own prints aren’t counted.'}
          </p>
        )}
        <div className="hr" />
      </div>

      {/* Preview. The full-screen copy replaces this one while it is up. */}
      {!taking && sheetArticle}

      {err && <p className="err noprint">{err}</p>}

      <div className="noprint">
        <div className="hr" />
        <button className="btn btn-quiet" onClick={() => nav(`/songs/${song.id}/learn`)}>
          Back to the song
        </button>
      </div>

      {/* Portalled to <body> rather than rendered in place, for two reasons that
          both bite here: the app's glass surfaces use backdrop-filter, which makes
          an ancestor a containing block and would trap position:fixed; and a
          <dialog> would put the sheet in the top layer, which browsers print
          inconsistently — and printing is the point. In the body it stays in
          normal flow, so the existing @media print rules keep working untouched. */}
      {taking && createPortal(
        <div className="takeover" role="dialog" aria-modal="true"
             aria-label={kind === 'chart' ? 'Chord chart' : 'Lyric sheet'}>
          <div className="takeover-bar noprint">
            <button className="takeover-x" onClick={() => setTaking(false)} aria-label="Close">
              ✕
            </button>
            <span className="takeover-name">{toTitleCase(song.title) || 'Untitled'}</span>
            <button className="btn btn-primary takeover-save" onClick={() => window.print()}>
              Save as PDF
            </button>
          </div>
          <div className="takeover-scroll">
            {sheetArticle}
            <p className="takeover-hint noprint">
              Choose “Save as PDF” as the destination to keep a copy, or a printer for paper.
            </p>
          </div>
        </div>,
        document.body,
      )}
    </Shell>
  )
}
